import { useEffect, useState } from "react";
import axios from "axios";
import { Bell, Mic, Music, Play } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useMe } from "@/context/MeContext";
import { API } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { LiveBadge } from "@/components/Equalizer";

const day = (iso) => new Date(iso).toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" });
const time = (iso) => new Date(iso).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });

function RemindDialog({ show, onClose }) {
  const { me } = useMe();
  const [email, setEmail] = useState(me?.email || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/shows/${show.id}/remind`, { email });
      const done = JSON.parse(localStorage.getItem("rp_reminders") || "[]");
      localStorage.setItem("rp_reminders", JSON.stringify([...done, show.id]));
      toast.success("¡Listo! Te avisaremos por email cuando empiece.");
      onClose(true);
    } catch (err) { toast.error(err?.response?.data?.detail || "No pudimos guardar el aviso"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent data-testid="remind-dialog" className="border-white/10 bg-[#12121A] text-slate-200">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white">Avisarme de “{show.title}”</DialogTitle>
          <DialogDescription className="text-slate-400">Te mandamos un solo email cuando el programa salga al aire.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex gap-2">
          <Input data-testid="remind-email-input" type="email" required placeholder="tu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="border-white/10 bg-[#181824] text-white" />
          <button data-testid="remind-submit-btn" disabled={busy} className="shrink-0 rounded-full bg-[#FF4500] px-5 text-sm font-semibold text-white disabled:opacity-60">{busy ? "..." : "Avisarme"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ShowCard({ s }) {
  const [asking, setAsking] = useState(false);
  const [reminded, setReminded] = useState(() => JSON.parse(localStorage.getItem("rp_reminders") || "[]").includes(s.id));
  const { playChannel } = usePlayer();
  return (
    <div data-testid={`show-card-${s.id}`} className={`overflow-hidden rounded-xl border ${s.is_live ? "border-[#FF4500]/60" : "border-white/10"} bg-[#12121A]`}>
      <div className="relative h-32">
        <img src={s.cover_url} alt="" className="h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#12121A] to-transparent" />
        <div className="absolute left-4 top-4">{s.is_live ? <LiveBadge /> : <span className="font-mono text-xs text-white">{time(s.start_at)} – {time(s.end_at)}</span>}</div>
      </div>
      <div className="p-5">
        <p className="font-mono text-[10px] tracking-widest text-amber-500/90">{s.channel_name?.toUpperCase()}</p>
        <h3 className="mt-1 font-heading text-xl font-bold text-white">{s.title}</h3>
        {s.host_name && <p className="text-sm text-slate-300">con {s.host_name}</p>}
        {s.description && <p className="mt-2 line-clamp-2 text-sm text-slate-400">{s.description}</p>}
        <p className="mt-3 flex items-center gap-3 font-mono text-[11px] text-slate-500">
          <span className="flex items-center gap-1"><Music className="h-3 w-3" />{s.track_count} canciones</span>
          {s.message_count > 0 && <span className="flex items-center gap-1"><Mic className="h-3 w-3" />{s.message_count} mensajes</span>}
        </p>
        {!s.is_live && (
          <button data-testid={`show-remind-${s.id}`} disabled={reminded} onClick={() => setAsking(true)} className="mt-4 flex items-center gap-2 rounded-full border border-white/20 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/10 disabled:opacity-60">
            <Bell className="h-4 w-4" /> {reminded ? "Te avisaremos" : "Avisarme"}
          </button>
        )}
        {asking && <RemindDialog show={s} onClose={(ok) => { setAsking(false); if (ok) setReminded(true); }} />}
        {s.is_live && (
          <button data-testid={`show-listen-${s.id}`} onClick={() => playChannel({ slug: s.channel_slug, name: s.channel_name })} className="mt-4 flex items-center gap-2 rounded-full bg-[#FF4500] px-5 py-2 text-sm font-semibold text-white">
            <Play className="h-4 w-4 fill-white" /> Escuchar en vivo
          </button>
        )}
      </div>
    </div>
  );
}

export default function Shows() {
  const [shows, setShows] = useState(null);
  useEffect(() => { axios.get(`${API}/shows`).then((r) => setShows(r.data)).catch(() => setShows([])); }, []);
  const groups = (shows || []).reduce((acc, s) => { const k = day(s.start_at); (acc[k] = acc[k] || []).push(s); return acc; }, {});
  return (
    <div data-testid="shows-page" className="mx-auto max-w-[1400px] px-4 py-16 md:px-8">
      <LiveBadge label="PROGRAMAS EN VIVO" />
      <h1 className="mt-5 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">Programación</h1>
      <p className="mt-4 max-w-xl text-slate-400">Programas con horario, armados por nuestros curadores. Cuando un programa está en vivo, todos escuchan lo mismo al mismo tiempo.</p>
      {shows && !shows.length && <p data-testid="shows-empty" className="mt-12 text-slate-500">No hay programas agendados por ahora. Mientras tanto, los canales suenan 24/7.</p>}
      {Object.entries(groups).map(([k, list]) => (
        <section key={k} className="mt-12">
          <h2 className="font-heading text-xl font-bold capitalize text-white">{k}</h2>
          <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{list.map((s) => <ShowCard key={s.id} s={s} />)}</div>
        </section>
      ))}
    </div>
  );
}
